import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import useStorefrontCurrency from "@features/ecommerce/hooks/useStorefrontCurrency";
import { getShiComBaseUrl } from "@lib/shiComBaseUrl";
import type CatalogedProduct from "shared-ui/src/types/ecommerce/CatalogedProduct";
import type { RecommendedAddOnItemDto } from "@lib/ShiOneClient";
import useAddShoppingCartProduct from "@features/ecommerce/hooks/useAddShoppingCartProduct";
import useAttachWarranty from "@features/ecommerce/cart/useAttachWarranty";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";

const baseUrl = getShiComBaseUrl();

// The list opens with three warranties so the modal stays compact, and one click reveals the
// rest. The API caps the section at ten, so "the rest" is bounded without a step size here.
const WARRANTY_VISIBLE_DEFAULT = 3;

/**
 * Stable radio value for a warranty option. Config-system warranties are identified by their
 * base-system option; catalog (commodity) warranties are plain products and have no option id.
 */
function warrantyOptionValue(item: RecommendedAddOnItemDto): string {
  return item.baseSystemOptionId != null
    ? `option-${item.baseSystemOptionId}`
    : `product-${item.productDescriptor}`;
}

// A care pack lists its coverage term as a bullet of its own ("3 years"). Only a bullet that is
// nothing but a duration is treated as the term: a phrase such as "parts and labor (for notebook
// with 1 year warranty)" describes the device's factory warranty, not the length of this add-on.
const TERM_ONLY_BULLET = /^(\d{1,2}) *years?\.?$/i;

/**
 * Coverage term of a warranty ("Term: 3 Years"), or undefined when the description carries no
 * term, or more than one candidate term and so cannot be read unambiguously.
 */
export function warrantyTermLabel(item: RecommendedAddOnItemDto): string | undefined {
  const terms = new Set(
    (item.parsedDescriptionsList ?? []).flatMap((bullet) => {
      const years = TERM_ONLY_BULLET.exec(bullet.trim())?.[1];
      return years ? [years] : [];
    }),
  );

  if (terms.size !== 1) {
    return undefined;
  }

  const [years] = [...terms];
  return `Term: ${years} ${years === "1" ? "Year" : "Years"}`;
}

export interface AddOnsPanelProps {
  /** The just-added device the add-ons are recommended for. */
  product: CatalogedProduct;
  /** Quantity of the device that was added (warranty qty is synced to this). */
  quantity: number;
  /** Prevents concurrent add-on changes while the device quantity is updating. */
  disabled?: boolean;
  /** Allows the parent dialog to prevent a concurrent device quantity update. */
  onWarrantyMutationChange?: (isMutating: boolean) => void;
  accessories?: RecommendedAddOnItemDto[];
  warranties?: RecommendedAddOnItemDto[];
  accessoriesHasMore?: boolean;
  /** How many warranties the product has in total, which labels "View all (n)". */
  warrantiesTotalCount?: number;
}

function AccessoryRow({ item, disabled }: { item: RecommendedAddOnItemDto; disabled?: boolean }) {
  const { formatAmount } = useStorefrontCurrency();
  const { mutate, isLoading } = useAddShoppingCartProduct();
  const [added, setAdded] = useState(false);

  const handleAdd = () => {
    const asProduct: CatalogedProduct = {
      id: `${item.productDescriptor}|${item.productClass}`,
      productDescriptor: item.productDescriptor,
      productClass: item.productClass,
      name: item.productName,
      manufacturerPartNumber: item.manufacturerPartNumber,
      imageUrl: item.imageUrl,
      price: item.price,
      availability: item.inventoryStatus,
    };
    mutate(asProduct, {
      onSuccess: () => {
        setAdded(true);
      },
    });
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.25, py: 1 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {item.imageUrl ? (
          <Box
            component="img"
            src={item.imageUrl}
            alt={item.productName ?? "Accessory"}
            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : (
          <ImageOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item.productName}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
          {item.inventoryStatus && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <FiberManualRecordIcon
                aria-hidden="true"
                sx={{
                  fontSize: 9,
                  color: item.inventoryStatus.toLowerCase().includes("in stock")
                    ? "success.main"
                    : item.inventoryStatus.toLowerCase().includes("backorder")
                      ? "error.main"
                      : "text.disabled",
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {item.inventoryStatus}
              </Typography>
            </Box>
          )}
          {item.inventoryStatus && (
            <Typography variant="caption" color="text.disabled">
              {"\u00b7"}
            </Typography>
          )}
          <Typography variant="caption" fontWeight={500} color="secondary.main">
            {formatAmount(Number(item.price ?? 0))}
          </Typography>
        </Box>
      </Box>
      <Button
        size="small"
        variant="text"
        startIcon={<AddShoppingCartIcon sx={{ fontSize: 14 }} />}
        disabled={disabled || added || isLoading}
        onClick={handleAdd}
        aria-label={`${added ? "Added" : "Add"} ${item.productName ?? "accessory"}`}
        {...telemetryProps({
          name: "add_to_cart_add_accessory",
          section: "add_to_cart",
          label: "Add Accessory",
        })}
      >
        {added ? "Added" : "Add"}
      </Button>
    </Box>
  );
}

/**
 * Accessories + warranty add-ons upsell rendered inside the add-to-cart modal (SPU-535).
 *
 * Accessories can be added inline (each row's button flips to "Added"; the modal stays
 * open and does not refresh). Warranties are optional and single-select via radios:
 * re-clicking the selected radio clears the selection, and the list opens with three and
 * reveals the rest (the API caps a section at ten) in a single click. Once expanded it can
 * be collapsed back to three, or handed off to the SHI.com product page for the full list.
 * A warranty the catalog cannot price is listed but not selectable, matching SHI.com.
 * A config-system warranty attaches to the device and its quantity is synced to the device
 * quantity server-side; a commodity warranty is a catalog SKU with no attach mechanism, so
 * it is added as an ordinary cart line at the device quantity (SPU-568). A capped accessory
 * section links to the product detail page for the full list.
 */
export default function AddOnsPanel({
  product,
  quantity,
  disabled,
  onWarrantyMutationChange,
  accessories,
  warranties,
  accessoriesHasMore,
  warrantiesTotalCount,
}: AddOnsPanelProps) {
  const { formatAmount } = useStorefrontCurrency();
  const attachWarranty = useAttachWarranty();
  const addWarrantyProduct = useAddShoppingCartProduct();
  const [selectedWarranty, setSelectedWarranty] = useState<string>("");
  const [warrantyAdded, setWarrantyAdded] = useState(false);
  const [showAllWarranties, setShowAllWarranties] = useState(false);

  const hasAccessories = (accessories?.length ?? 0) > 0;
  const hasWarranties = (warranties?.length ?? 0) > 0;
  const warrantyCount = warranties?.length ?? 0;
  const visibleWarranties = showAllWarranties
    ? (warranties ?? [])
    : (warranties?.slice(0, WARRANTY_VISIBLE_DEFAULT) ?? []);
  const isWarrantyListCollapsible = warrantyCount > WARRANTY_VISIBLE_DEFAULT;
  // The product can carry more warranties than the API returns, so the SHI.com product page is
  // offered with the true total rather than the length of this list.
  const totalWarranties = Math.max(warrantiesTotalCount ?? 0, warrantyCount);
  const warrantyMutating = attachWarranty.isLoading || addWarrantyProduct.isLoading;

  useEffect(() => {
    onWarrantyMutationChange?.(warrantyMutating);
  }, [warrantyMutating, onWarrantyMutationChange]);

  useEffect(
    () => () => onWarrantyMutationChange?.(false),
    [onWarrantyMutationChange],
  );

  if (!hasAccessories && !hasWarranties) {
    return null;
  }

  const handleAddWarranty = () => {
    const selected = warranties?.find(
      (w) => warrantyOptionValue(w) === selectedWarranty,
    );
    if (!selected) {
      return;
    }

    const onSuccess = () => {
      setWarrantyAdded(true);
    };

    if (selected.baseSystemId != null && selected.baseSystemOptionId != null) {
      attachWarranty.mutate(
        {
          baseSystemId: selected.baseSystemId,
          baseSystemOptionId: selected.baseSystemOptionId,
          quantity,
        },
        { onSuccess },
      );
      return;
    }

    if (!selected.productDescriptor || !selected.productClass) {
      return;
    }

    addWarrantyProduct.mutate(
      {
        id: `${selected.productDescriptor}|${selected.productClass}`,
        productDescriptor: selected.productDescriptor,
        productClass: selected.productClass,
        name: selected.productName,
        manufacturerPartNumber: selected.manufacturerPartNumber,
        imageUrl: selected.imageUrl,
        price: selected.price,
        availability: selected.inventoryStatus,
        quantity,
      },
      { onSuccess },
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 2 }} />
      {hasWarranties && (
        <Box
          component="section"
          aria-labelledby="add-warranty-heading"
          sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
        >
          <Typography id="add-warranty-heading" variant="subtitle2" fontWeight={500} sx={{ mb: 0.75 }}>
            Add a Warranty
          </Typography>
          <RadioGroup
            value={selectedWarranty}
            onChange={(e) => setSelectedWarranty(e.target.value)}
            sx={{ width: "100%" }}
          >
            <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", bgcolor: "background.paper" }}>
              {visibleWarranties.map((item, index) => {
                const termLabel = warrantyTermLabel(item);
                return (
                  <Box key={warrantyOptionValue(item)}>
                    {index > 0 && <Divider />}
                    <FormControlLabel
                      value={warrantyOptionValue(item)}
                      // A warranty the catalog cannot price is shown but cannot be ordered.
                      disabled={disabled || warrantyAdded || !item.canAddToCart}
                      control={
                        <Radio
                          size="small"
                          // A warranty is optional, so clicking the current selection clears it.
                          onClick={() => {
                            if (selectedWarranty === warrantyOptionValue(item)) {
                              setSelectedWarranty("");
                            }
                          }}
                        />
                      }
                      label={
                        <Box sx={{ width: "100%" }}>
                          <Typography variant="body2">{item.productName}</Typography>
                          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                            {termLabel && (
                              <Typography variant="caption" color="text.secondary">
                                {termLabel}
                              </Typography>
                            )}
                            <Typography variant="caption" fontWeight={500} color="secondary.main">
                              {item.price == null
                                ? "Price unavailable"
                                : formatAmount(item.price)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        m: 0,
                        px: 1,
                        py: 0.5,
                        "& .MuiFormControlLabel-label": { flex: 1, minWidth: 0 },
                      }}
                      {...telemetryProps({
                        name: "add_to_cart_select_warranty",
                        section: "add_to_cart",
                        label: "Select Warranty",
                      })}
                    />
                  </Box>
                );
              })}
              <Divider />
              <Box
                sx={{
                  px: 1.25,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                {/* Reveal controls sit on the left so the Add button lines up with the Add
                    button on every Recommended Add-ons row. */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {isWarrantyListCollapsible &&
                    (showAllWarranties ? (
                      <>
                        <Button
                          size="small"
                          variant="text"
                          endIcon={<RemoveIcon sx={{ fontSize: 14 }} />}
                          disabled={disabled}
                          onClick={() => setShowAllWarranties(false)}
                          {...telemetryProps({
                            name: "add_to_cart_view_less_warranties",
                            section: "add_to_cart",
                            label: "View Less Warranties",
                          })}
                        >
                          VIEW LESS
                        </Button>
                        {product.productDescriptor && (
                          <Button
                            size="small"
                            variant="text"
                            href={`${baseUrl}/product/${product.productDescriptor}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                            {...telemetryProps({
                              name: "add_to_cart_view_all_warranties",
                              section: "add_to_cart",
                              label: "View All Warranties",
                            })}
                          >
                            {`VIEW ALL (${totalWarranties})`}
                          </Button>
                        )}
                      </>
                    ) : (
                      <Button
                        size="small"
                        variant="text"
                        endIcon={<AddIcon sx={{ fontSize: 14 }} />}
                        disabled={disabled}
                        onClick={() => setShowAllWarranties(true)}
                        {...telemetryProps({
                          name: "add_to_cart_view_more_warranties",
                          section: "add_to_cart",
                          label: "View More Warranties",
                        })}
                      >
                        VIEW MORE
                      </Button>
                    ))}
                </Box>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<AddShoppingCartIcon sx={{ fontSize: 14 }} />}
                  disabled={disabled || !selectedWarranty || warrantyAdded || warrantyMutating}
                  onClick={handleAddWarranty}
                  aria-label={warrantyAdded ? "Warranty added" : "Add warranty"}
                  {...telemetryProps({
                    name: "add_to_cart_add_warranty",
                    section: "add_to_cart",
                    label: "Add Warranty",
                  })}
                >
                  {warrantyAdded ? "ADDED" : "ADD"}
                </Button>
              </Box>
            </Box>
          </RadioGroup>
        </Box>
      )}

      {hasAccessories && hasWarranties && <Divider sx={{ my: 2 }} />}

      {hasAccessories && (
        <Box component="section" aria-labelledby="recommended-add-ons-heading">
          <Typography id="recommended-add-ons-heading" variant="subtitle2" fontWeight={500} sx={{ mb: 0.75 }}>
            Recommended Add-ons
          </Typography>
          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", bgcolor: "background.paper" }}>
            {accessories!.map((item, index) => (
              <Box key={`${item.productDescriptor}|${item.productClass}`}>
                {index > 0 && <Divider />}
                <AccessoryRow
                  item={item}
                  disabled={disabled}
                />
              </Box>
            ))}
            {accessoriesHasMore && product.productDescriptor && (
              <>
                <Divider />
                <Box sx={{ px: 1.25, py: 1 }}>
                  <Button
                    href={`${baseUrl}/product/${product.productDescriptor}#accessories`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="text"
                    size="small"
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    {...telemetryProps({
                      name: "add_to_cart_view_all_add_ons",
                      section: "add_to_cart",
                      label: "View All Add-ons",
                    })}
                  >
                    VIEW ALL ADD-ONS
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
